import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Upload, FileSpreadsheet, Loader2, Trash2, Award, X, FileUp, ImagePlus, Pencil } from "lucide-react";

const normAseg = (s) => (s || "").normalize("NFD").toLowerCase().replace(/[^a-z0-9]/g, "");

export const ComparativoCoberturaIA = () => {
  const { getAuthHeaders } = useAuth();
  const [comparativos, setComparativos] = useState([]);
  const [logos, setLogos] = useState({});
  const [files, setFiles] = useState([]);
  const [titulo, setTitulo] = useState("");
  const [analizando, setAnalizando] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [recoDialog, setRecoDialog] = useState(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [recoForm, setRecoForm] = useState({ afiliado_nombre: "", edad_titular: "", dependientes: "", edades_dependientes: "", condiciones: "", costos: {} });
  const [editDialog, setEditDialog] = useState(null);
  const [editPlanes, setEditPlanes] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [logoTarget, setLogoTarget] = useState(null);

  const fetchComparativos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comparativo-cobertura/`, getAuthHeaders());
      setComparativos(res.data.comparativos || []);
    } catch (e) { /* silencioso */ }
  }, [getAuthHeaders]);

  const fetchLogos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comparativo-cobertura/logos`, getAuthHeaders());
      const map = {};
      (res.data.logos || []).forEach((l) => { map[l.nombre_normalizado] = l.data_url; });
      setLogos(map);
    } catch (e) { /* silencioso */ }
  }, [getAuthHeaders]);

  useEffect(() => { fetchComparativos(); fetchLogos(); }, [fetchComparativos, fetchLogos]);

  const agregarArchivos = (nuevos) => {
    const pdfs = nuevos.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (pdfs.length !== nuevos.length) toast.error("Solo se permiten archivos PDF");
    setFiles((prev) => {
      const combinados = [...prev, ...pdfs];
      if (combinados.length > 5) toast.error("Máximo 5 planes por comparativo");
      return combinados.slice(0, 5);
    });
  };

  const handleAnalizar = async () => {
    if (files.length < 2) { toast.error("Suba al menos 2 PDFs de planes"); return; }
    setAnalizando(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (titulo) fd.append("titulo", titulo);
      await axios.post(`${API}/comparativo-cobertura/analizar`, fd, { ...getAuthHeaders(), timeout: 300000 });
      toast.success("Comparativo generado por la IA");
      setFiles([]); setTitulo("");
      fetchComparativos();
      fetchLogos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error analizando los PDFs");
    } finally { setAnalizando(false); }
  };

  const subirLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !logoTarget) return;
    try {
      const fd = new FormData();
      fd.append("aseguradora", logoTarget);
      fd.append("file", f);
      await axios.post(`${API}/comparativo-cobertura/logos`, fd, getAuthHeaders());
      toast.success(`Logo de ${logoTarget} guardado para futuras comparaciones`);
      fetchLogos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error subiendo el logo");
    } finally { setLogoTarget(null); }
  };

  const descargarExcel = async (comp) => {
    try {
      const res = await axios.get(`${API}/comparativo-cobertura/${comp.id}/excel`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(comp.titulo || "comparativo").replace(/ /g, "_")}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error descargando el Excel"); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este comparativo?")) return;
    try {
      await axios.delete(`${API}/comparativo-cobertura/${id}`, getAuthHeaders());
      fetchComparativos();
    } catch (e) { toast.error("Error eliminando"); }
  };

  const abrirReco = (comp) => {
    const d = comp.datos_afiliado || {};
    setRecoForm({
      afiliado_nombre: d.afiliado_nombre || "", edad_titular: d.edad_titular || "",
      dependientes: d.dependientes || "", edades_dependientes: d.edades_dependientes || "",
      condiciones: d.condiciones || "", costos: d.costos || {}
    });
    setRecoDialog(comp);
  };

  const abrirEdicion = (comp) => {
    setEditPlanes((comp.planes || []).map((p) => ({ nombre: p.nombre || "", aseguradora: p.aseguradora || "", coberturas: { ...(p.coberturas || {}) } })));
    setEditDialog(comp);
  };

  const guardarEdicion = async () => {
    setEditLoading(true);
    try {
      await axios.put(`${API}/comparativo-cobertura/${editDialog.id}/coberturas`, { planes: editPlanes }, getAuthHeaders());
      toast.success("Coberturas actualizadas");
      setEditDialog(null);
      fetchComparativos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando los cambios");
    } finally { setEditLoading(false); }
  };

  const setCelda = (planIdx, fila, valor) => {
    setEditPlanes((prev) => prev.map((p, i) => i === planIdx ? { ...p, coberturas: { ...p.coberturas, [fila]: valor } } : p));
  };

  const pedirRecomendacion = async () => {
    if (!recoForm.afiliado_nombre.trim()) { toast.error("Indique el nombre del afiliado"); return; }
    setRecoLoading(true);
    try {
      await axios.post(`${API}/comparativo-cobertura/${recoDialog.id}/recomendar`, recoForm, { ...getAuthHeaders(), timeout: 180000 });
      toast.success("Recomendación generada");
      setRecoDialog(null);
      fetchComparativos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando la recomendación");
    } finally { setRecoLoading(false); }
  };

  return (
    <Card className="mb-8 border-[#0C314C]/20" data-testid="comparativo-cobertura-ia-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#0C314C]" /> Comparativo de Cobertura IA
        </CardTitle>
        <CardDescription>
          Suba los PDFs de 2 a 5 planes: la IA llena el cuadro comparativo, lo descarga en Excel y puede recomendar el mejor plan para su cliente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Título (cliente/propuesta)</Label>
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ej: Propuesta Juan Pérez" data-testid="comp-ia-titulo" className="mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label>PDFs de los planes (2-5)</Label>
            <div
              data-testid="comp-ia-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); agregarArchivos(Array.from(e.dataTransfer.files || [])); }}
              className={`mt-1 border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition-colors ${dragOver ? "border-[#0C314C] bg-[#0C314C]/5" : "border-slate-300 dark:border-slate-600 hover:border-[#0C314C]/60"}`}
            >
              <FileUp className={`w-6 h-6 mx-auto mb-1 ${dragOver ? "text-[#0C314C]" : "text-slate-400"}`} />
              <p className="text-sm text-slate-600 dark:text-slate-300">Arrastre y suelte los PDFs aquí, o haga clic para seleccionarlos</p>
              <p className="text-xs text-slate-400 mt-0.5">{files.length > 0 ? `${files.length} archivo(s) seleccionado(s)` : "Solo PDF · máximo 5 planes"}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                className="hidden"
                onChange={(e) => { agregarArchivos(Array.from(e.target.files || [])); e.target.value = ""; }}
                data-testid="comp-ia-files"
              />
            </div>
          </div>
        </div>
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {files.map((f, i) => (
              <Badge key={i} variant="outline" className="gap-1">
                {f.name}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFiles(files.filter((_, j) => j !== i))} />
              </Badge>
            ))}
          </div>
        )}
        <Button onClick={handleAnalizar} disabled={analizando || files.length < 2} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="comp-ia-analizar-btn">
          {analizando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
          {analizando ? "Analizando con IA..." : "Analizar y Comparar"}
        </Button>

        <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={subirLogo} data-testid="comp-ia-logo-file" />

        {comparativos.length > 0 && (
          <div className="space-y-3">
            {comparativos.map((c) => (
              <div key={c.id} className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800" data-testid={`comp-ia-item-${c.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold">{c.titulo}</p>
                    <p className="text-xs text-slate-500">
                      {(c.planes || []).map((p) => p.nombre).join(" vs ")} · {new Date(c.created_at).toLocaleDateString()}
                      {c.created_by_name ? ` · ${c.created_by_name}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => abrirEdicion(c)} data-testid={`comp-ia-editar-${c.id}`}>
                      <Pencil className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => descargarExcel(c)} data-testid={`comp-ia-excel-${c.id}`}>
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => abrirReco(c)} data-testid={`comp-ia-reco-${c.id}`}>
                      <Award className="w-4 h-4 mr-1" /> {c.recomendacion ? "Ver/Actualizar Recomendación" : "Recomendar Plan"}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => eliminar(c.id)} data-testid={`comp-ia-del-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(c.planes || []).map((p) => {
                    const lg = logos[normAseg(p.aseguradora)];
                    return (
                      <span key={p.nombre} className="flex items-center gap-1.5 text-xs border rounded px-2 py-1 bg-white dark:bg-slate-900">
                        {lg ? (
                          <img src={lg} alt={p.aseguradora} className="h-5 w-auto max-w-[70px] object-contain" />
                        ) : (
                          <button
                            className="flex items-center gap-0.5 text-[10px] text-[#0C314C] dark:text-blue-300 underline"
                            onClick={() => { setLogoTarget(p.aseguradora); logoInputRef.current?.click(); }}
                            data-testid={`comp-ia-subir-logo-${normAseg(p.aseguradora)}`}
                            title={`Subir logo de ${p.aseguradora} (se guarda para futuras comparaciones)`}
                          >
                            <ImagePlus className="w-3 h-3" /> Subir logo
                          </button>
                        )}
                        <span className="text-slate-600 dark:text-slate-300">{p.nombre}</span>
                      </span>
                    );
                  })}
                </div>
                {c.recomendacion && (
                  <div className="mt-3 p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800" data-testid={`comp-ia-reco-box-${c.id}`}>
                    <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                      Plan recomendado: {c.recomendacion.plan_recomendado}
                      {c.datos_afiliado?.afiliado_nombre && ` — para ${c.datos_afiliado.afiliado_nombre}`}
                    </p>
                    <p className="text-sm mt-1">{c.recomendacion.justificacion}</p>
                    {(c.recomendacion.consideraciones || []).length > 0 && (
                      <ul className="text-xs text-slate-600 dark:text-slate-400 mt-2 list-disc ml-4">
                        {c.recomendacion.consideraciones.map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Editar Coberturas — {editDialog?.titulo}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto flex-1 border rounded-md">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#0C314C] text-white z-10">
                <tr>
                  <th className="p-2 text-left min-w-[220px]">Cobertura</th>
                  {editPlanes.map((p, i) => (
                    <th key={i} className="p-2 min-w-[200px]">
                      <Input
                        value={p.nombre}
                        onChange={(e) => setEditPlanes((prev) => prev.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x))}
                        className="h-7 text-xs bg-white text-slate-900"
                        data-testid={`edit-plan-nombre-${i}`}
                      />
                      <Input
                        value={p.aseguradora}
                        onChange={(e) => setEditPlanes((prev) => prev.map((x, j) => j === i ? { ...x, aseguradora: e.target.value } : x))}
                        className="h-7 text-xs bg-white text-slate-900 mt-1"
                        placeholder="Aseguradora"
                        data-testid={`edit-plan-aseg-${i}`}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(editPlanes[0]?.coberturas || {}).map((fila) => (
                  <tr key={fila} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                    <td className="p-2 font-medium text-slate-700 dark:text-slate-300">{fila}</td>
                    {editPlanes.map((p, i) => (
                      <td key={i} className="p-1">
                        <Textarea
                          rows={1}
                          value={p.coberturas[fila] || ""}
                          onChange={(e) => setCelda(i, fila, e.target.value)}
                          className="min-h-[32px] text-xs resize-y"
                          data-testid={`edit-celda-${i}-${fila.slice(0, 20)}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Cancelar</Button>
            <Button onClick={guardarEdicion} disabled={editLoading} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="edit-guardar-btn">
              {editLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recoDialog} onOpenChange={(o) => !o && setRecoDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Recomendación de Plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre del afiliado *</Label>
              <Input value={recoForm.afiliado_nombre} onChange={(e) => setRecoForm({ ...recoForm, afiliado_nombre: e.target.value })} data-testid="reco-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Edad del titular</Label>
                <Input value={recoForm.edad_titular} onChange={(e) => setRecoForm({ ...recoForm, edad_titular: e.target.value })} placeholder="Ej: 45" data-testid="reco-edad" />
              </div>
              <div>
                <Label>Dependientes</Label>
                <Input value={recoForm.dependientes} onChange={(e) => setRecoForm({ ...recoForm, dependientes: e.target.value })} placeholder="Ej: esposa y 2 hijos" data-testid="reco-dependientes" />
              </div>
            </div>
            <div>
              <Label>Edades de los dependientes</Label>
              <Input value={recoForm.edades_dependientes} onChange={(e) => setRecoForm({ ...recoForm, edades_dependientes: e.target.value })} placeholder="Ej: 42, 10 y 7" data-testid="reco-edades-dep" />
            </div>
            <div>
              <Label>Condiciones médicas o de salud</Label>
              <Textarea rows={2} value={recoForm.condiciones} onChange={(e) => setRecoForm({ ...recoForm, condiciones: e.target.value })} placeholder="Ej: titular hipertenso, hija con asma" data-testid="reco-condiciones" />
            </div>
            <div>
              <Label>Costo anual de cada plan (opcional)</Label>
              {(recoDialog?.planes || []).map((p) => (
                <div key={p.nombre} className="flex items-center gap-2 mt-1">
                  <span className="text-xs w-44 truncate">{p.nombre}</span>
                  <Input
                    className="h-8"
                    placeholder="Ej: US$4,500"
                    value={recoForm.costos[p.nombre] || ""}
                    onChange={(e) => setRecoForm({ ...recoForm, costos: { ...recoForm.costos, [p.nombre]: e.target.value } })}
                    data-testid={`reco-costo-${p.nombre}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecoDialog(null)}>Cancelar</Button>
            <Button onClick={pedirRecomendacion} disabled={recoLoading} className="bg-[#0C314C] hover:bg-[#0a2940] text-white" data-testid="reco-generar-btn">
              {recoLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
              Generar Recomendación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ComparativoCoberturaIA;
